/* ===========================================================================
 *  Wat de app, de Worker en de functie cowork met elkaar uitwisselen
 *
 *  Eén bestand voor beide kanten, zodat "het scherm verwacht een veld dat de
 *  server nooit stuurt" een typefout is en geen lege plek op het scherm.
 *
 *  De functie cowork staat in de dashboard-repo (supabase/functions/cowork)
 *  en kan dit bestand niet importeren. Daar staan dezelfde vormen nog een
 *  keer, met een verwijzing hierheen. Verander je hier iets, verander het
 *  daar ook.
 *
 *  Tijden zijn overal epoch-milliseconden, zoals in de rest van het dashboard.
 * =========================================================================== */

/* ------------------------------------------------------------------ *
 *  Wie
 * ------------------------------------------------------------------ */

export interface Ik {
  /** Het dossier-id uit profiles; hetzelfde nummer als in het dashboard. */
  id: string
  naam: string
  email: string
  rollen: string[]
  vestiging: string | null
}

/**
 * Waar iemand bij kan, per onderwerp.
 *
 * Uitgerekend door de database ALS deze persoon -- niet door de functie die
 * het nareken. Zie supabase/functions/cowork/als.ts in de dashboard-repo.
 * Staat `ja` op false, dan kan Cowork daar ook niets over vinden.
 */
export interface Toegang {
  sleutel: string
  naam: string
  ja: boolean
  /** Een korte zin, bijvoorbeeld "23 administraties" of "alleen je eigen". */
  uitleg?: string | null
}

export interface Administratie {
  code: string
  naam: string
}

/** Hoe het met de eigen AI op de pc op kantoor staat. */
export interface AiStatus {
  /** De pc meldde zich de laatste twee minuten. */
  online: boolean
  laatst: number | null
  model: string
  /** Waar hij nu mee bezig is, als hij iets doet ("een factuur lezen"). */
  bezig: string | null
  /** Hoeveel AI-opdrachten er nog liggen, van iedereen samen. */
  wachtrij: number
}

export interface IkAntwoord {
  ok: true
  ik: Ik
  toegang: Toegang[]
  administraties: Administratie[]
  ai: AiStatus
  /** Voorbeeldvragen die passen bij wat deze persoon mag zien. */
  voorbeelden: string[]
  /**
   * Welke Cowork-rechten deze persoon heeft (functie cowork, rechten.ts;
   * migratie 0198): cowork.bijlagen, cowork.terugkerend, cowork.mailen, …
   * Het scherm verbergt wat uitstaat; de server controleert het hoe dan ook.
   */
  rechten?: string[]
  /** Wanneer de sessie verloopt. */
  verlooptOp: number
}

/* ------------------------------------------------------------------ *
 *  Gesprekken
 * ------------------------------------------------------------------ */

export interface GesprekMeta {
  id: string
  titel: string
  vastgezet: boolean
  /** Gevuld als dit het gesprek van een terugkerende taak is. */
  taakId: string | null
  aangemaakt: number
  bijgewerkt: number
  /** De eerste regel van het laatste bericht, voor in de lijst. */
  laatste: string | null
}

export type Rol = 'gebruiker' | 'assistent'

/**
 * Eén stap die de AI zette om bij een antwoord te komen: een tool die hij
 * aanriep. Het scherm toont ze als "Zocht in inkoopfacturen · 12 gevonden".
 */
export interface Stap {
  id: string
  /** De naam van de tool, bijvoorbeeld "facturen". */
  tool: string
  /** Wat er op het scherm staat terwijl hij bezig is: "Zoekt in inkoopfacturen". */
  label: string
  /** Waarmee hij werd aangeroepen, zoals de AI het vroeg. */
  args: Record<string, unknown>
  status: 'bezig' | 'klaar' | 'fout'
  /** Na afloop: "12 gevonden", "geen toegang", "3 administraties". */
  samenvatting?: string | null
  ms?: number | null
}

/**
 * Iets wat de AI wil DOEN in plaats van zeggen.
 *
 * Casper: "alles wat de AI invult moet te corrigeren zijn". Dus voert de AI
 * niets zelf uit. Hij zet een voorstel klaar; het scherm toont het als een
 * kaart met de velden open om aan te passen, en pas een druk op "Uitvoeren"
 * doet het -- als de gebruiker, met zijn eigen rechten.
 */
export interface Voorstel {
  id: string
  soort: 'taak' | 'terugkerend' | 'ticket' | 'relatie' | 'relatie-wijzigen' | 'exact'
  /** Een korte zin bovenaan de kaart: "Taak voor Wim: banden bestellen". */
  titel: string
  /** Bij soort 'taak' een TaakVelden, bij 'terugkerend' een TerugkerendVelden, bij 'ticket' een TicketVelden, bij 'relatie' een RelatieVelden, bij 'exact' een ExactVelden. */
  velden: TaakVelden | TerugkerendVelden | TicketVelden | RelatieVelden | RelatieWijzigVelden | ExactVelden
  /** bezig: wordt op dit moment uitgevoerd (0207: één voorstel, één keer). */
  status: 'open' | 'bezig' | 'uitgevoerd' | 'afgewezen' | 'mislukt'
  /** Na uitvoeren: wat er gebeurde, of waarom het niet lukte. */
  uitkomst?: string | null
}

/**
 * Een relatie in Exact (migratie 0177, _gedeeld/exact-relatie.ts in de
 * dashboard-repo). `controle` is wat de server zag bij het voorstel of bij
 * "Opnieuw controleren"; bij Uitvoeren kijkt hij het zelf nog een keer na.
 */
export interface RelatieGegevens {
  naam: string
  soort: 'leverancier' | 'klant' | 'beide'
  kvk: string
  btw: string
  adres: string
  postcode: string
  plaats: string
  land: string
  email: string
  telefoon: string
  website: string
  iban: string
  bic: string
  tenaamstelling: string
  betaalconditieInkoop: string
  betaalconditieVerkoop: string
  notitie: string
}

export interface RelatieTreffer {
  id: string
  code: string
  naam: string
  plaats: string
  op: ('btw' | 'kvk' | 'naam' | 'iban')[]
  /** Hetzelfde IBAN bij een relatie met een andere naam. */
  verdacht: boolean
}

export interface RelatieControle {
  fouten: Record<string, string>
  waarschuwingen: string[]
  perBv: { division: string; naam: string; treffers: RelatieTreffer[]; fout: string | null }[]
}

/**
 * Een bestaande relatie wijzigen of inactief zetten. `huidig` is wat Exact
 * zei toen Cowork het voorstel maakte; welke relatie en welke bv staat vast
 * (de server neemt die uit het opgeslagen voorstel, niet van de kaart).
 */
export interface RelatieWijzigVelden {
  division: string
  bvNaam: string
  id: string
  code: string
  modified: string
  huidig: { relatie: RelatieGegevens; actief: boolean; geblokkeerd: boolean }
  relatie: RelatieGegevens
  actief: boolean
  geblokkeerd: boolean
  tweelingen: { division: string; bvNaam: string; id: string; code: string; naam: string; op: string }[]
  ook: string[]
  ibanBevestigd: boolean
  /** Helemaal weg uit Exact (alleen zonder boekingen). */
  verwijderen: boolean
  wat: string[]
  waarschuwingen: string[]
  nieuweIban: boolean
  uitkomsten?: { division: string; naam: string; code: string; relatieNaam: string; hoe: 'gewijzigd' | 'ongewijzigd' | 'verwijderd' | 'mislukt'; wat: string[]; fout: string | null; meldingen: string[] }[]
}

export interface RelatieVelden {
  relatie: RelatieGegevens
  divisies: string[]
  mogelijk: { code: string; naam: string }[]
  toch: string[]
  controle: RelatieControle | null
  vies: string | null
  uitkomsten?: {
    division: string
    naam: string
    hoe: 'gemaakt' | 'overgeslagen' | 'mislukt'
    relatie: { division: string; id: string; code: string; naam: string; bankFout: string | null } | null
    fout: string | null
    meldingen: string[]
  }[]
}

/* ------------------------------------------------------------------ *
 *  Schrijven in Exact (0219): grootboek, kostenplaatsen, boekingen en
 *  verkoopfacturen
 *
 *  Dezelfde vormen als supabase/functions/_gedeeld/exact-schrijven/
 *  soorten.ts in de dashboard-repo. Verander je hier iets, verander het
 *  daar ook (en andersom).
 *
 *  Codes zoals de gebruiker ze in Exact ziet (rekening "4130", dagboek
 *  "60", btw-code "2"), nooit guids: die zoekt de server zelf op, per bv.
 * ------------------------------------------------------------------ */

export type Functie = 'grootboek' | 'kostenplaats' | 'inkoop' | 'verkoop' | 'memoriaal' | 'bank' | 'kas' | 'verkoopfactuur'

export const FUNCTIES: Functie[] = ['grootboek', 'kostenplaats', 'inkoop', 'verkoop', 'memoriaal', 'bank', 'kas', 'verkoopfactuur']

/** Het recht per functie (0219). Bank en kas delen er een. */
export const RECHT_VAN: Record<Functie, string> = {
  grootboek: 'exact.grootboek',
  kostenplaats: 'exact.kostenplaatsen',
  inkoop: 'exact.inkoopboeken',
  verkoop: 'exact.verkoopboeken',
  memoriaal: 'exact.memoriaal',
  bank: 'exact.bankkas',
  kas: 'exact.bankkas',
  verkoopfactuur: 'exact.verkoopfacturen',
}

/** Een verkoopfactuur VERSTUREN naar de klant is een recht apart. */
export const RECHT_VERSTUREN = 'exact.facturen_versturen'

/** Wat er op de kaart als titel boven staat. */
export const NAAM_VAN: Record<Functie, string> = {
  grootboek: 'Grootboekrekening',
  kostenplaats: 'Kostenplaats',
  inkoop: 'Inkoopboeking',
  verkoop: 'Verkoopboeking',
  memoriaal: 'Memoriaalboeking',
  bank: 'Bankboeking',
  kas: 'Kasboeking',
  verkoopfactuur: 'Verkoopfactuur',
}

/** Grootboek en kostenplaats kunnen in meer bv's tegelijk; een boeking of factuur in precies één. */
export const MEER_BVS: Record<Functie, boolean> = {
  grootboek: true, kostenplaats: true,
  inkoop: false, verkoop: false, memoriaal: false, bank: false, kas: false, verkoopfactuur: false,
}

export interface GrootboekGegevens {
  actie: 'aanmaken' | 'wijzigen' | 'blokkeren' | 'deblokkeren'
  /** Het rekeningnummer, bijv. "4135". */
  code: string
  omschrijving: string
  /** Een bestaande rekening om soort, kant, type en btw van over te nemen ("zoals 4130"). */
  zoals?: string
  /** W = winst-en-verlies, B = balans (Exact: BalanceType). */
  soort: 'W' | 'B' | ''
  /** D = debet, C = credit (Exact: BalanceSide). */
  kant: 'D' | 'C' | ''
  /** Het type in Exact (GLAccounts.Type), bijv. 110 omzet; null = nog niet gekozen. */
  type: number | null
  /** Standaard btw-code op deze rekening (leeg = geen). */
  btw?: string
}

export interface KostenplaatsGegevens {
  soort: 'kostenplaats' | 'kostendrager'
  actie: 'aanmaken' | 'wijzigen' | 'blokkeren' | 'deblokkeren'
  code: string
  omschrijving: string
}

export interface BoekRegel {
  omschrijving: string
  /** Grootboekrekening (code). */
  rekening: string
  /**
   * Inkoop en verkoop: het bedrag EXCLUSIEF btw, positief (een creditnota: negatief).
   * Memoriaal: + is debet, - is credit.
   * Bank, kas: gelezen vanuit de bank of kas: + is geld erbij (ontvangst),
   * - is geld eraf (betaling).
   */
  bedrag: number
  /** Btw-code (inkoop, verkoop; bij memoriaal mag het). Leeg = geen btw. */
  btw?: string
  kostenplaats?: string
  kostendrager?: string
  /** Bank, kas, memoriaal: de relatie (code) waar deze regel bij hoort. */
  relatie?: string
  /** Bank, kas: de datum van deze regel (jjjj-mm-dd); leeg = de boekdatum. */
  datum?: string
}

export interface BoekingGegevens {
  soort: 'inkoop' | 'verkoop' | 'memoriaal' | 'bank' | 'kas'
  /** Dagboekcode in Exact (leeg = de server kiest het standaarddagboek van die soort). */
  dagboek: string
  /** Boekdatum, jjjj-mm-dd. */
  datum: string
  /** Boekjaar en -periode; leeg = volgt uit de datum. */
  jaar?: number | null
  periode?: number | null
  /** Inkoop: de leverancier, verkoop: de klant (relatiecode in Exact). */
  relatie?: string
  omschrijving: string
  /** Inkoop: het factuurnummer van de leverancier. Verkoop: ons factuurnummer / kenmerk. */
  referentie?: string
  vervaldatum?: string
  /** Leeg = EUR. */
  valuta?: string
  regels: BoekRegel[]
  /** Inkoop/verkoop: het totaal INCLUSIEF btw zoals op de factuur, alleen ter controle. */
  totaal_incl?: number | null
}

export interface FactuurRegel {
  /** Artikelcode in Exact. */
  artikel: string
  omschrijving?: string
  /** null = nog niet bekend (dan een fout op de kaart, nooit stil 0). */
  aantal: number | null
  /** Prijs per stuk EXCLUSIEF btw; null = nog niet bekend. */
  prijs: number | null
  btw?: string
  kostenplaats?: string
}

export interface VerkoopfactuurGegevens {
  /** Klant (relatiecode in Exact). */
  klant: string
  /** Verkoopdagboek; leeg = het standaard verkoopdagboek. */
  dagboek: string
  /** Factuurdatum, jjjj-mm-dd. */
  datum: string
  omschrijving: string
  /** Kenmerk van de klant (bijv. hun inkoopordernummer). */
  referentie?: string
  valuta?: string
  regels: FactuurRegel[]
  /** Na het aanmaken ook naar de klant mailen (via Exact). Apart recht: exact.facturen_versturen. */
  versturen: boolean
}

export type ExactGegevens = GrootboekGegevens | KostenplaatsGegevens | BoekingGegevens | VerkoopfactuurGegevens

export interface Controle {
  /** Zolang hier iets staat, kan Uitvoeren niet. */
  fouten: string[]
  /** Moet de gebruiker weten, houdt niets tegen. */
  waarschuwingen: string[]
}

export interface Keuze { code: string; naam: string; extra?: string }

/** Keuzelijsten voor de kaart, voor de (eerste) gekozen bv. */
export interface Keuzes {
  dagboeken?: Keuze[]
  btw?: Keuze[]
  kostenplaatsen?: Keuze[]
  kostendragers?: Keuze[]
  artikelen?: Keuze[]
}

/** Wat de server uitrekende of opzocht, om te tonen (niet om te bewerken). */
export interface Berekend {
  /**
   * Code -> naam van wat op de kaart staat, met de soort ervoor want codes
   * botsen (dagboek "70" en artikel "70"): "rekening:4130", "dagboek:60",
   * "btw:3", "relatie:1234", "kostenplaats:VENLO", "kostendrager:X",
   * "artikel:WAS01".
   */
  namen?: Record<string, string>
  /**
   * Inkoop, verkoop en verkoopfactuur: excl, btw, incl. Memoriaal: debet,
   * credit, verschil. Bank en kas: ontvangen, betaald, saldo.
   */
  totalen?: Record<string, number>
  /** Per bv (grootboek/kostenplaats): bestaat de code daar al, en hoe. */
  perBv?: { division: string; naam: string; bestaat: boolean; omschrijving?: string; geblokkeerd?: boolean }[]
}

/**
 * Wat er per bv gebeurde. Kan ok zijn MET een fout erbij: gemaakt, maar een
 * stap erna mislukte (een verkoopfactuur aangemaakt, versturen niet). Het
 * scherm toont dan allebei.
 */
export interface Uitkomst {
  division: string
  bvNaam: string
  ok: boolean
  /** Wat er gebeurde, in een zin ("Boeking 26600123 gemaakt in dagboek 60"). */
  wat: string
  /** Boekstuknummer, rekeningcode of factuurnummer. */
  nummer?: string | null
  /** Het ID in Exact. */
  id?: string | null
  fout?: string | null
}

/** De velden van een voorstel met soort 'exact'. */
export interface ExactVelden {
  functie: Functie
  gegevens: ExactGegevens
  /** De gekozen bv's (codes). Bij MEER_BVS[functie] = false precies één. */
  divisies: string[]
  /** Waar deze persoon dit mag (voor de vinkjes of de keuzelijst). */
  mogelijk: { code: string; naam: string }[]
  keuzes?: Keuzes
  berekend?: Berekend
  controle: Controle
  /** Wat de server aanvulde of aannam ("Dagboek 60 gekozen: het enige inkoopdagboek"). */
  uitleg?: string[]
  uitkomsten?: Uitkomst[]
}

/**
 * Een melding bij IT (it.truckwash-workspace.com, migratie 0165). Gemeld
 * ALS de gebruiker: de database zet hem als melder en stuurt IT een seintje.
 * Spoed kan hier niet; dat kiest IT zelf.
 */
export interface TicketVelden {
  titel: string
  omschrijving: string
  categorie: 'hardware' | 'account' | 'netwerk' | 'printer' | 'telefoon' | 'software' | 'mail' | 'toegang' | 'beveiliging' | 'overig'
  prioriteit: 'laag' | 'normaal' | 'hoog'
}

export interface TaakVelden {
  titel: string
  omschrijving: string
  /** Dossier-id; leeg = voor jezelf. */
  toegewezenAan: string | null
  toegewezenNaam: string | null
  /** jjjj-mm-dd of leeg. */
  deadline: string | null
  prioriteit: 'laag' | 'normaal' | 'hoog' | 'urgent'
}

export interface TerugkerendVelden {
  naam: string
  opdracht: string
  frequentie: Frequentie
  /** HH:MM, Nederlandse tijd. */
  tijd: string
  /** 1 = maandag … 7 = zondag. Alleen bij 'wekelijks'. */
  dagen: number[]
  /** 1–28. Alleen bij 'maandelijks'. */
  dagVanMaand: number | null
  /** jjjj-mm-dd. Alleen bij 'eenmalig'. */
  datum: string | null
  melden: boolean
  mailen: boolean
}

/**
 * Een bron waar het antwoord op steunt: een gevonden factuur, een relatie.
 * `link` is een adres in het dashboard als we dat kennen.
 */
export interface Bron {
  soort: string
  titel: string
  bij?: string | null
  link?: string | null
}

/* ------------------------------------------------------------------ *
 *  Bestanden
 *
 *  Twee kanten op. Een gebruiker kan bestanden uploaden die Cowork leest
 *  (PDF, Excel, CSV, Word, PowerPoint, tekst, foto's, ZIP), en Cowork kan
 *  bestanden maken die je downloadt (Excel, CSV, PDF, Word, Markdown, ZIP).
 *
 *  Casper: "als je iets laat uploaden, zorg dat je het niet opslaat, nog niet
 *  in ieder geval, zorg ervoor dat je die melding ook doorgeeft."
 *
 *  Dus: een UPLOAD wordt niet bewaard. De server leest hem uit en houdt de
 *  inhoud alleen vast voor de vraag waar hij bij hoort (hoogstens een uur);
 *  na het antwoord is hij weg. In het gesprek blijft alleen de naam staan.
 *  Een GEMAAKT bestand blijft 7 dagen te downloaden en wordt dan opgeruimd.
 *
 *  Uploaden:   POST /api/upload?naam=<bestandsnaam>&gesprekId=<id of leeg>
 *              met de ruwe bytes als lijf en het type als Content-Type.
 *              Antwoord: { ok: true, bestand: Bestand }. Hoogstens 15 MB.
 *              Daarna gaat het id mee in `bijlagen` van de actie vraag.
 *  Downloaden: GET /api/bestand/<id>            als download (alleen gemaakt)
 *              GET /api/bestand/<id>?inline=1   om te bekijken (pdf, foto)
 * ------------------------------------------------------------------ */

export interface Bestand {
  id: string
  naam: string
  mime: string
  grootte: number
  /** 'gemaakt' door Cowork, of 'geupload' door de gebruiker. */
  soort: 'gemaakt' | 'geupload'
  at: number
  /** Waar hij te downloaden is: /api/bestand/<id>. Leeg bij een upload: die wordt niet bewaard. */
  url: string | null
  /** false bij een upload: alleen gelezen voor deze vraag, daarna weg. */
  bewaard: boolean
  /** Tot wanneer een gemaakt bestand te downloaden is. */
  verlooptOp: number | null
  /**
   * Hoe Cowork hem kon lezen, bij een upload:
   *   tekst  de tekst is eruit gehaald
   *   tabel  een spreadsheet, per blad
   *   beeld  een foto of een scan; de AI kijkt ernaar
   *   niet   dit type kan Cowork niet lezen (wel bewaren en terugsturen)
   */
  leesbaar?: 'tekst' | 'tabel' | 'beeld' | 'niet' | null
  /** Een korte regel: "3 bladen, 1.240 rijen" of "12 pagina's". */
  samenvatting?: string | null
}

export interface Bericht {
  id: string
  rol: Rol
  /** Markdown. Bij de assistent het antwoord, bij de gebruiker de vraag. */
  tekst: string
  at: number
  stappen?: Stap[]
  voorstellen?: Voorstel[]
  bronnen?: Bron[]
  /** Bij de gebruiker: wat hij meestuurde. Bij de assistent: wat Cowork maakte. */
  bestanden?: Bestand[]
  model?: string | null
  /** Hoe lang het antwoord duurde, van vraag tot antwoord. */
  duurMs?: number | null
  /** Gesteld door een terugkerende taak in plaats van door een mens. */
  automatisch?: boolean
  /** Het antwoord is een foutmelding, geen antwoord. */
  fout?: boolean
}

export type BeurtStatus =
  | 'wacht'      // opdracht ligt in de wachtrij voor de pc
  | 'denkt'      // de pc heeft hem opgepakt
  | 'verwerken'  // antwoord binnen, de server zet de volgende stap
  | 'tool'       // de server haalt gegevens op
  | 'klaar'
  | 'mislukt'
  | 'gestopt'

/** Hoe het staat met één vraag die loopt. Het scherm vraagt dit om de paar seconden op. */
export interface BeurtStand {
  id: string
  gesprekId: string
  status: BeurtStatus
  stappen: Stap[]
  /** Hoeveel AI-opdrachten er vóór deze in de wachtrij staan. */
  voor?: number | null
  /** Staat de pc op kantoor aan? */
  online?: boolean | null
  /** Zodra hij klaar (of mislukt) is: het antwoord zoals het in het gesprek komt. */
  bericht?: Bericht | null
  fout?: string | null
  sinds: number
}

export interface Gesprek extends GesprekMeta {
  berichten: Bericht[]
  /** Een vraag die nog loopt, zodat terugkomen in het gesprek hem verder volgt. */
  lopend: BeurtStand | null
}

/* ------------------------------------------------------------------ *
 *  Terugkerende taken
 * ------------------------------------------------------------------ */

export type Frequentie = 'dagelijks' | 'werkdagen' | 'wekelijks' | 'maandelijks' | 'eenmalig'

export interface TerugkerendeTaak extends TerugkerendVelden {
  id: string
  actief: boolean
  /** Waar de uitkomsten in komen: één gesprek per taak. */
  gesprekId: string | null
  /** Wanneer hij de volgende keer draait; leeg als hij uit staat of klaar is. */
  volgende: number | null
  laatst: number | null
  laatstStatus: 'klaar' | 'mislukt' | 'bezig' | 'overgeslagen' | null
  laatstUitleg: string | null
  aangemaakt: number
  bijgewerkt: number
}

/* ------------------------------------------------------------------ *
 *  Collega's, voor het toewijzen van een taak
 * ------------------------------------------------------------------ */

export interface Mens {
  id: string
  naam: string
  functie: string | null
}

/* ------------------------------------------------------------------ *
 *  De acties, met wat erin gaat en wat eruit komt
 *
 *  Alles gaat als POST /api/c met { actie, ...lijf }. Het antwoord heeft
 *  altijd ok: true, of ok: false met een fout die op het scherm kan.
 * ------------------------------------------------------------------ */

export interface Acties {
  ik:                  { in: Record<string, never>; uit: IkAntwoord }
  'ai-online':         { in: Record<string, never>; uit: { ok: true; ai: AiStatus } }
  gesprekken:          { in: { q?: string }; uit: { ok: true; gesprekken: GesprekMeta[] } }
  gesprek:             { in: { id: string }; uit: { ok: true; gesprek: Gesprek } }
  'gesprek-hernoemen': { in: { id: string; titel: string }; uit: { ok: true; gesprek: GesprekMeta } }
  'gesprek-vastzetten':{ in: { id: string; vast: boolean }; uit: { ok: true; gesprek: GesprekMeta } }
  'gesprek-weg':       { in: { id: string }; uit: { ok: true } }
  /** Een vraag stellen. Zonder gesprekId begint er een nieuw gesprek. */
  vraag:               { in: { gesprekId?: string | null; tekst: string; bijlagen?: string[]; spraak?: boolean }; uit: { ok: true; gesprek: GesprekMeta; vraag: Bericht; beurt: BeurtStand } }
  stand:               { in: { beurtId: string }; uit: { ok: true; beurt: BeurtStand } }
  stop:                { in: { beurtId: string }; uit: { ok: true; beurt: BeurtStand } }
  /** Het laatste antwoord opnieuw laten maken. */
  opnieuw:             { in: { gesprekId: string }; uit: { ok: true; beurt: BeurtStand; weg: string | null } }
  /** Een voorstel uitvoeren (met de velden zoals de gebruiker ze aanpaste) of afwijzen. */
  voorstel:            { in: { berichtId: string; voorstelId: string; besluit: 'uitvoeren' | 'afwijzen'; velden?: TaakVelden | TerugkerendVelden | TicketVelden | RelatieVelden | RelatieWijzigVelden | ExactVelden }; uit: { ok: true; voorstel: Voorstel } }
  /** Wat een wijzig-voorstel nu zou veranderen, met de velden zoals ze op de kaart staan. */
  'relatie-wijzigen-nakijken': { in: { division: string; id: string; modified: string; relatie: RelatieGegevens; actief: boolean; geblokkeerd: boolean }; uit: { ok: true; wat: string[]; waarschuwingen: string[]; nieuweIban: boolean; verouderd: boolean } }
  /** De kaart van een relatie-voorstel opnieuw laten nakijken, met de velden zoals ze nu zijn. */
  'relatie-controleren': { in: { relatie: RelatieGegevens; divisies: string[] }; uit: { ok: true; controle: RelatieControle & { relatie: RelatieGegevens } } }
  /**
   * Een Exact-kaart opnieuw laten nakijken, met wat er NU op staat. De server
   * neemt functie en mogelijk uit het OPGESLAGEN voorstel (niet van de kaart),
   * kijkt gegevens en divisies opnieuw na en geeft de bijgewerkte velden terug:
   * gegevens (zoals hij ze netjes maakte), divisies, keuzes, berekend,
   * controle en uitleg. ok: false alleen als er niets na te kijken viel.
   */
  'exact-controleren': { in: { berichtId: string; voorstelId: string; velden: ExactVelden }; uit: { ok: boolean; velden: ExactVelden } }
  taken:               { in: Record<string, never>; uit: { ok: true; taken: TerugkerendeTaak[] } }
  'taak-opslaan':      { in: { taak: Partial<TerugkerendeTaak> & TerugkerendVelden }; uit: { ok: true; taak: TerugkerendeTaak } }
  'taak-weg':          { in: { id: string }; uit: { ok: true } }
  /** Een terugkerende taak nu meteen laten draaien, buiten zijn schema om. */
  'taak-nu':           { in: { id: string }; uit: { ok: true; gesprekId: string; beurt: BeurtStand } }
  mensen:              { in: Record<string, never>; uit: { ok: true; mensen: Mens[] } }
  /** Alle bestanden van een gesprek (of van jou, zonder gesprekId), nieuwste eerst. */
  bestanden:           { in: { gesprekId?: string | null }; uit: { ok: true; bestanden: Bestand[] } }
  /** Een geupload bestand weghalen dat nog niet is verstuurd, of een oud bestand. */
  'bestand-weg':       { in: { id: string }; uit: { ok: true } }
}

export type ActieNaam = keyof Acties
